import React, { useState } from "react";
import { createPortal } from "react-dom";

import useSearchPopover from "../useSearchPopover";
import "./badgeSelect.scss";

const POP_W = 240;
const POP_MAXH = 300;

/**
 * Searchable colour-pill picker — the same UX as the Weekly Task Kanban card
 * badges: click the coloured pill and a floating searchable list appears
 * (flips up/down to fit the viewport); pick an option.
 *
 * Reuse anywhere we currently have a colour-badge dropdown (status / type /
 * payment term, etc.).
 *
 * Props:
 *  - current:     selected option object ({ Id, Name, CssColourBackground, CssColourText })
 *  - list:        array of option objects
 *  - onSelect:    (id) => void — called with the chosen option's Id
 *  - placeholder: search box placeholder (default "Search…")
 *  - emptyLabel:  trigger text when nothing is selected (default "—")
 *  - sortByName:  sort options alphabetically (default true)
 *  - allowClear:  show a "clear" option at the top of the list that calls
 *                 onSelect(clearValue) — mirrors StaffSelect (Task 24327)
 */
export default function BadgeSelect({
  current,
  list,
  onSelect,
  placeholder = "Search…",
  emptyLabel = "—",
  sortByName = true,
  allowClear = false,
  clearLabel = "None",
  clearValue = "",
}) {
  const [q, setQ] = useState("");
  const {
    open,
    setOpen,
    coords,
    anchorRef,
    popRef,
    openMenu,
    guardSelect,
    autoFocus,
  } = useSearchPopover({ width: POP_W, maxHeight: POP_MAXH });

  const handleOpen = (e) => {
    setQ("");
    openMenu(e);
  };

  const choose = guardSelect((id) => {
    onSelect?.(id);
    setOpen(false);
  });

  const stop = (e) => e.stopPropagation();

  let options = (list || []).filter((o) =>
    (o?.Name || "").toLowerCase().includes(q.toLowerCase())
  );
  if (sortByName) {
    options = [...options].sort((a, b) =>
      (a?.Name || "").localeCompare(b?.Name || "")
    );
  }

  return (
    <>
      <span
        ref={anchorRef}
        className="badge-select__pill"
        style={{
          background: current?.CssColourBackground || "#F3EEFC",
          color: current?.CssColourText || "#865CE6",
        }}
        onClick={handleOpen}
        title="Click to change"
      >
        {current?.Name || emptyLabel}
        <i className="ri-arrow-down-s-line badge-select__caret" />
      </span>

      {open &&
        coords &&
        createPortal(
          <div
            ref={popRef}
            className="badge-select__pop"
            style={{
              position: "fixed",
              left: coords.left,
              top: coords.top,
              bottom: coords.bottom,
              width: POP_W,
            }}
            // Portaled to <body>, but React still bubbles through the component
            // tree — without this, picking an option also fires the row's onClick.
            onMouseDown={stop}
            onClick={stop}
          >
            <div className="badge-select__search">
              <i className="ri-search-line" />
              <input
                autoFocus={autoFocus}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={placeholder}
              />
            </div>
            <div
              className="badge-select__list"
              style={{ maxHeight: coords.maxHeight }}
            >
              {allowClear && !q && (
                <button
                  type="button"
                  className={`badge-select__item${
                    current ? "" : " is-active"
                  }`}
                  onClick={() => choose(clearValue)}
                >
                  <span
                    className="badge-select__pill"
                    style={{ background: "#F1F5F9", color: "#64748B" }}
                  >
                    {clearLabel}
                  </span>
                  {!current && (
                    <i className="ri-check-line badge-select__check" />
                  )}
                </button>
              )}
              {options.length === 0 && (
                <div className="badge-select__empty">No matches</div>
              )}
              {options.map((opt) => (
                <button
                  key={opt.Id}
                  type="button"
                  className={`badge-select__item${
                    opt.Id === current?.Id ? " is-active" : ""
                  }`}
                  onClick={() => choose(opt.Id)}
                >
                  <span
                    className="badge-select__pill"
                    style={{
                      background: opt?.CssColourBackground || "#F3EEFC",
                      color: opt?.CssColourText || "#865CE6",
                    }}
                  >
                    {opt.Name}
                  </span>
                  {opt.Id === current?.Id && (
                    <i className="ri-check-line badge-select__check" />
                  )}
                </button>
              ))}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
